import React from 'react';
import {
  Briefcase,
  Shirt,
  Sparkles,
  UtensilsCrossed,
  Monitor,
  Dumbbell,
  Gem,
  Plane,
  Building2,
  HeartPulse,
  Car,
  Baby,
  PawPrint,
  Music,
  Camera,
  Home,
  GraduationCap,
  Leaf,
  type LucideIcon,
} from 'lucide-react';
import { Category } from '../../types';
import { useI18n } from '../../context/I18nContext';
import { getTranslatedCategoryLabel } from '../../utils/categoryTranslations';
import Tooltip from './Tooltip';

interface SectorIconProps {
  category?: Category | null;
  className?: string;
  /** Outer avatar circle classes */
  avatarClassName?: string;
  showTooltip?: boolean;
}

/** Concat FR+EN labels so keyword match works regardless of UI language. */
function sectorMatchText(category?: Category | null): string {
  if (!category) return '';
  return [category.label_fr, category.label_en, category.label]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** Map sector label (FR/EN) to an icon — keyword match, Briefcase as default (not Building). */
export function getSectorLucideIcon(category?: Category | null): LucideIcon {
  const label = sectorMatchText(category);
  if (!label) return Briefcase;

  if (/mode|fashion|textile|vêtement|vetement|apparel|clothing/.test(label)) return Shirt;
  if (/beauté|beaute|beauty|cosm[eé]tique|makeup|skincare|soin/.test(label)) return Sparkles;
  if (/food|foodtech|restaurant|cuisine|agro|alimentaire|gastronom/.test(label)) return UtensilsCrossed;
  if (/tech|digital|saas|software|informatique|it\b|web\b/.test(label)) return Monitor;
  if (/sport|fitness|athl[eè]te|wellness/.test(label)) return Dumbbell;
  if (/luxe|luxury|joaill|jewelry|horloger/.test(label)) return Gem;
  if (/voyage|travel|touris|h[oô]tel/.test(label)) return Plane;
  if (/immo|real\s*estate|property|habitat/.test(label)) return Building2;
  if (/sant[eé]|health|m[eé]dical|pharma/.test(label)) return HeartPulse;
  if (/auto|automobile|mobility|mobilit[eé]/.test(label)) return Car;
  if (/enfant|kids|baby|pu[eé]riculture/.test(label)) return Baby;
  if (/animal|pet|v[eé]t[eé]rinaire/.test(label)) return PawPrint;
  if (/musique|music|audio|entertainment|divertissement/.test(label)) return Music;
  if (/photo|vid[eé]o|film|cin[eé]ma|m[eé]dia|media|cr[eé]a/.test(label)) return Camera;
  if (/maison|home|d[eé]co|interior|lifestyle/.test(label)) return Home;
  if (/[eé]ducat|formation|school|learning/.test(label)) return GraduationCap;
  if (/eco|green|environnement|sustainab|rse/.test(label)) return Leaf;

  return Briefcase;
}

/**
 * Avatar icon for a lead sector (replaces generic building in lead pickers).
 */
const SectorIcon: React.FC<SectorIconProps> = ({
  category,
  className = 'w-4 h-4 text-white',
  avatarClassName = 'w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0',
  showTooltip = true,
}) => {
  const { t, language } = useI18n();
  const Icon = getSectorLucideIcon(category);
  const label = category ? getTranslatedCategoryLabel(category, t, language).trim() : '';
  const avatar = (
    <div className={avatarClassName} aria-hidden={!label}>
      <Icon className={className} />
    </div>
  );

  if (showTooltip && label) {
    return <Tooltip content={label}>{avatar}</Tooltip>;
  }

  return avatar;
};

export default SectorIcon;
